/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, memo, useEffect } from 'react';
import TransactionsContext from './TransactionsContext';
import initialTransactionsState from './initialTransactionsState';
import transactionsReducer from 'contexts/transactions/reducer/transactions.reducer';
import * as action from 'contexts/transactions/reducer/transactions.actions';
import { useAuth0 } from '@auth0/auth0-react';
import useContextCompany from 'hooks/contexts/useContextCompany';
import { abortCatalogController } from 'utils/abortController';
import { useTranslation } from 'react-i18next';
import { IuseTableColumnSearchV2PropsSearchItems } from 'hooks/useTableColumnSearchV2';
import { DropResult } from 'react-beautiful-dnd';
import { ChildrenProps } from 'interfaces/general.interface';

function TransactionsProvider(props: ChildrenProps) {
  const [transactionsState, dispatch] = useReducer(
    transactionsReducer,
    initialTransactionsState
  );
  const { getAccessTokenSilently } = useAuth0();
  const { company } = useContextCompany();
  const { t: translate } = useTranslation();

  // DeliveryNotes ////////////////////////////////////////////////////////////////////
  useEffect(() => {
    (async () => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.getDeliveryNotesColumnsAction({
        dispatch,
        token,
        translate,
      });
    })();
  }, []);

  const getDeliveryNotesItems = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (!token || !company?.id) return false;
    const signal = await abortCatalogController();
    return await action.getDeliveryNotesItemsAction({
      dispatch,
      deliveryNotesBodyFetch: transactionsState.deliveryNotesBodyFetch,
      clientId: company.id,
      signal,
      token,
      translate,
    });
  }, [company?.id]);

  const searchOnDeliveryNotesItems = useCallback(
    async ({
      searchField,
      searchText,
      searchType,
      type,
    }: IuseTableColumnSearchV2PropsSearchItems) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      const signal = await abortCatalogController();
      return await action.searchOnDeliveryNotesItemsAction({
        dispatch,
        catalogBodyFetch: transactionsState.deliveryNotesBodyFetch,
        searchOnCatalogItemsProps: {
          searchField,
          searchText,
          searchType,
          type,
        },
        signal,
        token,
        translate,
      });
    },
    [transactionsState]
  );

  const handleDeliveryNotesPagination = useCallback(
    async ({ page, pageSize }: { page: number; pageSize: number }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      const signal = await abortCatalogController();
      return await action.handleDeliveryNotesPaginationAction({
        dispatch,
        page,
        pageSize,
        transactionsState,
        signal,
        token,
        translate,
      });
    },
    [transactionsState]
  );

  const deliveryNotesMoveColumnsOrder = useCallback(
    (result: DropResult) => {
      return action.deliveryNotesMoveColumnsOrderAction({
        dispatch,
        result,
        deliveryNotesColumnsOrder: transactionsState.deliveryNotesColumnsOrder,
      });
    },
    [transactionsState.deliveryNotesColumnsOrder]
  );

  const deliveryNotesCheckColumn = useCallback(
    (columnKey: string) => {
      return action.deliveryNotesCheckColumnAction({
        dispatch,
        columnKey,
        deliveryNotesColumnsOrder: transactionsState.deliveryNotesColumnsOrder,
      });
    },
    [transactionsState.deliveryNotesColumnsOrder]
  );

  // Bills /////////////////////////////////////////////////////////////////////
  useEffect(() => {
    (async () => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.getBillsColumnsAction({
        dispatch,
        token,
        translate,
      });
    })();
  }, []);

  const getBillsItems = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (!token || !company?.id) return false;
    const signal = await abortCatalogController();
    return await action.getBillsItemsAction({
      dispatch,
      billsBodyFetch: transactionsState.billsBodyFetch,
      clientId: company.id,
      signal,
      token,
      translate,
    });
  }, [company?.id]);

  const searchOnBillsItems = useCallback(
    async ({
      searchField,
      searchText,
      searchType,
      type,
    }: IuseTableColumnSearchV2PropsSearchItems) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      const signal = await abortCatalogController();
      return await action.searchOnBillsItemsAction({
        dispatch,
        catalogBodyFetch: transactionsState.billsBodyFetch,
        searchOnCatalogItemsProps: {
          searchField,
          searchText,
          searchType,
          type,
        },
        signal,
        token,
        translate,
      });
    },
    [transactionsState]
  );

  const handleBillsPagination = useCallback(
    async ({ page, pageSize }: { page: number; pageSize: number }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      const signal = await abortCatalogController();
      return await action.handleBillsPaginationAction({
        dispatch,
        page,
        pageSize,
        transactionsState,
        signal,
        token,
        translate,
      });
    },
    [transactionsState]
  );

  const billsMoveColumnsOrder = useCallback(
    (result: DropResult) => {
      return action.billsMoveColumnsOrderAction({
        dispatch,
        result,
        billsColumnsOrder: transactionsState.billsColumnsOrder,
      });
    },
    [transactionsState.billsColumnsOrder]
  );

  const billsCheckColumn = useCallback(
    (columnKey: string) => {
      return action.billsCheckColumnAction({
        dispatch,
        columnKey,
        billsColumnsOrder: transactionsState.billsColumnsOrder,
      });
    },
    [transactionsState.billsColumnsOrder]
  );

  // Orders ////////////////////////////////////////////////////////////////////
  useEffect(() => {
    (async () => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.getOrdersColumnsAction({
        dispatch,
        token,
        translate,
      });
    })();
  }, []);

  const getOrdersItems = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (!token || !company?.id) return false;
    const signal = await abortCatalogController();
    return await action.getOrdersItemsAction({
      dispatch,
      ordersBodyFetch: transactionsState.ordersBodyFetch,
      clientId: company.id,
      signal,
      token,
      translate,
    });
  }, [company?.id]);

  const searchOnOrdersItems = useCallback(
    async ({
      searchField,
      searchText,
      searchType,
      type,
    }: IuseTableColumnSearchV2PropsSearchItems) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      const signal = await abortCatalogController();
      return await action.searchOnOrdersItemsAction({
        dispatch,
        catalogBodyFetch: transactionsState.ordersBodyFetch,
        searchOnCatalogItemsProps: {
          searchField,
          searchText,
          searchType,
          type,
        },
        signal,
        token,
        translate,
      });
    },
    [transactionsState]
  );

  const handleOrdersPagination = useCallback(
    async ({ page, pageSize }: { page: number; pageSize: number }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      const signal = await abortCatalogController();
      return await action.handleOrdersPaginationAction({
        dispatch,
        page,
        pageSize,
        transactionsState,
        signal,
        token,
        translate,
      });
    },
    [transactionsState]
  );

  const ordersMoveColumnsOrder = useCallback(
    (result: DropResult) => {
      return action.ordersMoveColumnsOrderAction({
        dispatch,
        result,
        ordersColumnsOrder: transactionsState.ordersColumnsOrder,
      });
    },
    [transactionsState.ordersColumnsOrder]
  );

  const ordersCheckColumn = useCallback(
    (columnKey: string) => {
      return action.ordersCheckColumnAction({
        dispatch,
        columnKey,
        ordersColumnsOrder: transactionsState.ordersColumnsOrder,
      });
    },
    [transactionsState.ordersColumnsOrder]
  );

  const memoProvider = useMemo(
    () => ({
      ...transactionsState,
      // Delivery Notes
      getDeliveryNotesItems,
      searchOnDeliveryNotesItems,
      handleDeliveryNotesPagination,
      deliveryNotesMoveColumnsOrder,
      deliveryNotesCheckColumn,
      // Bills
      getBillsItems,
      searchOnBillsItems,
      handleBillsPagination,
      billsMoveColumnsOrder,
      billsCheckColumn,
      // Orders
      getOrdersItems,
      searchOnOrdersItems,
      handleOrdersPagination,
      ordersMoveColumnsOrder,
      ordersCheckColumn,
    }),
    [
      transactionsState,
      // Delivery Notes
      getDeliveryNotesItems,
      searchOnDeliveryNotesItems,
      handleDeliveryNotesPagination,
      deliveryNotesMoveColumnsOrder,
      deliveryNotesCheckColumn,
      // Bills
      getBillsItems,
      searchOnBillsItems,
      handleBillsPagination,
      billsMoveColumnsOrder,
      billsCheckColumn,
      // Orders
      getOrdersItems,
      searchOnOrdersItems,
      handleOrdersPagination,
      ordersMoveColumnsOrder,
      ordersCheckColumn,
    ]
  );

  return (
    <TransactionsContext.Provider value={memoProvider}>
      {props.children}
    </TransactionsContext.Provider>
  );
}

export default memo(TransactionsProvider);
